import EmailIcon from '@mui/icons-material/Email';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { NotificationSettings as NotificationSettingsType } from '../../../types/user';

interface ProfileSectionProps {
    email: string;
    isGoogleUser: boolean;
    notificationSettings: NotificationSettingsType;
    isLoading: boolean;
    supportId?: string;
    onChangeEmail: () => void;
    onChangePassword: () => void;
    onUnlinkGoogle: () => void;
    onSettingChange: (key: keyof NotificationSettingsType) => void;
}

const ProfileSection: React.FC<ProfileSectionProps> = ({
    email,
    isGoogleUser,
    notificationSettings,
    isLoading,
    supportId,
    onChangeEmail,
    onChangePassword,
    onUnlinkGoogle,
    onSettingChange
}) => {
    const { t } = useTranslation();
    
    return (
        <Paper 
            variant="outlined" 
            sx={{ 
                p: 3, 
                bgcolor: 'background.paper',
                height: 'auto',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                    color: 'text.primary', 
                    fontWeight: 'bold',
                    fontSize: '1.25rem',
                    mb: 2
                }}
            >
                {t('settings.profile.title')}
            </Typography>
            
            <Stack spacing={3}>
                {/* Email section with buttons */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' }, 
                    justifyContent: 'space-between',
                    gap: 2
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        mb: { xs: 1, sm: 0 }
                    }}>
                        <Typography variant="body1" color="text.secondary" fontWeight="bold" sx={{ mr: 1.5, fontSize: '1rem' }}>
                            {t('settings.account.email')}:
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.primary', fontSize: '1rem' }}>
                            {email}
                        </Typography>
                    </Box>
                    
                    <Stack 
                        direction={{ xs: 'column', sm: 'row' }} 
                        spacing={1.5}
                        sx={{ 
                            alignSelf: { xs: 'flex-start', sm: 'center' },
                            width: { xs: '100%', sm: 'auto' }
                        }}
                    >
                        {isGoogleUser ? (
                            import.meta.env.MODE === 'developent' && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={onUnlinkGoogle}
                                sx={{ 
                                    minWidth: '160px',
                                    justifyContent: 'center'
                                }}
                            >
                                {t('settings.account.buttons.disconnectGoogle')}
                            </Button>)
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<EmailIcon />}
                                    onClick={onChangeEmail}
                                    sx={{ 
                                        minWidth: { xs: '100%', sm: '140px' },
                                        justifyContent: 'flex-start'
                                    }}
                                >
                                    {t('settings.account.buttons.changeEmail')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<LockIcon />}
                                    onClick={onChangePassword}
                                    sx={{ 
                                        minWidth: { xs: '100%', sm: '140px' },
                                        justifyContent: 'flex-start'
                                    }}
                                >
                                    {t('settings.account.buttons.changePassword')}
                                </Button>
                            </>
                        )}
                    </Stack>
                </Box>
                
                {/* User ID section with info icon */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    pt: 1
                }}>
                    <Typography variant="body1" color="text.secondary" fontWeight="bold" sx={{ mr: 1.5, fontSize: '1rem' }}>
                        User-ID:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                        {supportId || '-'}
                    </Typography>
                    <Tooltip 
                        title={
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                    {t('settings.support.description')}
                                </Typography>
                            </Box>
                        }
                        arrow
                        placement="right"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    maxWidth: 400,
                                    width: 'auto',
                                    p: 0,
                                    bgcolor: (theme) => theme.palette.background.paper,
                                    color: (theme) => theme.palette.text.primary,
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                    border: '1px solid',
                                    borderColor: (theme) => theme.palette.divider,
                                    borderRadius: 1,
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    '& .MuiTooltip-arrow': {
                                        color: (theme) => theme.palette.background.paper,
                                    }
                                },
                            },
                            popper: {
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        p: 0,
                                    }
                                }
                            }
                        }}
                    >
                        <InfoOutlinedIcon 
                            sx={{
                                ml: 0.5,
                                fontSize: '1.1rem',
                                color: 'text.secondary',
                                cursor: 'help',
                                position: 'relative',
                                top: '-1px'
                            }} 
                        />
                    </Tooltip>
                </Box>
                
                {/* Notification settings */}
                <Box sx={{ 
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 2,
                    pt: 1
                }}>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            lineHeight: 1.5,
                            pr: 2,
                            fontSize: '1rem'
                        }}
                    >
                        {t('settings.notifications.productUpdates')}
                    </Typography>
                    
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onSettingChange('productUpdates')}
                        disabled={isLoading}
                        startIcon={notificationSettings.productUpdates ? 
                            <MarkEmailReadIcon /> : 
                            <EmailOutlinedIcon />
                        }
                        sx={{
                            minWidth: '120px',
                            borderRadius: '4px',
                            textTransform: 'none',
                            color: notificationSettings.productUpdates ? 'primary.main' : 'text.primary',
                            borderColor: notificationSettings.productUpdates ? 'primary.main' : 'divider',
                            bgcolor: 'transparent',
                            height: '40px',
                            px: 1.5,
                            '&:hover': {
                                bgcolor: 'action.hover',
                                borderColor: notificationSettings.productUpdates ? 'primary.main' : 'divider'
                            }
                        }}
                    >
                        {notificationSettings.productUpdates ? (
                            t('settings.notifications.unsubscribe')
                        ) : (
                            t('settings.notifications.subscribe')
                        )}
                    </Button>
                </Box>
            </Stack>
        </Paper>
    );
};

export default ProfileSection;
